import { useGetEvaluatedInterviewsQuery } from "@/app/features/recruitment/interview/interview.api";
import GiveMark from "@/pages/Admin/Recruitment/Evaluation/_components/EvAssessment/GiveMark";
import ViewEvaluatedMarks from "@/pages/Admin/Recruitment/Evaluation/_components/EvInterview/ViewEvaluatedMarks";
import OfferLetterDrawer from "@/pages/Admin/Recruitment/Hiring/_components/OfferLetterDrawer";
import { Button, Flex, type TableProps } from "antd";
import CustomTable from "../../../../components/CustomTable";
import StatusTag from "../../../../components/StatusTag";
import {
  DISCLOSER_KEYS,
  FILTER_KEYS,
} from "../../../../constants/keys.constants";
import { useAppDiscloser, useAppFilters } from "../../../../hooks";
import SendOfferLetter from "./_components/SendOfferLetter";
import SetupInterview from "./_components/SetupInterview";
import { formatDate, formatTo12Hour, normalizeColumnValue } from "@/utils";

const INTERVIEW_STATUS_CONFIG = [
  { label: "Passed", value: "passed", className: "status status-success" },
  { label: "Failed", value: "failed", className: "status status-error" },
  { label: "No-Show", value: "no_show", className: "status status-error" },
  { label: "Pending", value: "pending", className: "status status-yellow" },
  {
    label: "In Progress",
    value: "in_progress",
    className: "status status-yellow",
  },
];

type TModal = "SETUP" | "EVALUATE" | "ASSESSMENT" | "OFFER" | "VIEW_OFFER";

export default function InterviewScores() {
  const { page, limit, keyword, filters, handlePagination } = useAppFilters(
    FILTER_KEYS.INTERVIEW_SCORES,
  );

  const [config, handleOpen, handleClose] = useAppDiscloser<TModal>(
    DISCLOSER_KEYS.INTERVIEW_SCORES,
  );

  const { data: evaluatedData, isFetching } = useGetEvaluatedInterviewsQuery({
    page,
    limit,
    search: keyword,
    // status: filters?.status,
    ...filters,
  });

  const columns: TableProps<any>["columns"] = [
    {
      title: "Applicant",
      dataIndex: "applicant_name",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Interviewer",
      dataIndex: "evaluator_name",
      align: "center",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Job Applied",
      dataIndex: "job_title",
      align: "center",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Interview Type",
      dataIndex: "interview_type",
      align: "center",
      render: (val: string) => normalizeColumnValue(val),
    },
    {
      title: "Date & Time",
      dataIndex: "interview_date",
      align: "center",
      render: (val: string, record: any) => {
        const date = formatDate(val);
        const time = formatTo12Hour(record.interview_time);

        return (
          <span>
            {date}
            {" - "}
            {time}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "interview_status",
      align: "center",
      width: 150,
      render: (val: string) => (
        <StatusTag value={val} config={INTERVIEW_STATUS_CONFIG} />
      ),
    },
    {
      title: "Assessment Marks",
      dataIndex: "assessment_obtained_mark",
      align: "center",
      render: (val: number, record: any) => {
        const total = record?.assessment_total_mark || 0;
        const percentage = total > 0 ? (val / total) * 100 : 0;
        return (
          <span>
            {val !== null && val !== undefined && val !== 0 ? (
              <Button
                type="default"
                className="text-[#00C6AE] border-none !bg-transparent shadow-none
              underline underline-offset-4 decoration-[#00C6AE] p-0"
                onClick={() =>
                  handleOpen({
                    type: "ASSESSMENT",
                    data: {
                      id: record?.assignment_id,
                      applicant_name: record?.applicant_name,
                    },
                  })
                }
              >
                {percentage.toFixed(0)}%
              </Button>
            ) : (
              <div>N/A</div>
            )}
          </span>
        );
      },
    },
    {
      title: "Interview Marks",
      dataIndex: "interview_obtained_mark",
      align: "center",
      render: (val: string, record: any) => {
        const total = Number(record?.interview_total_mark) || 0;
        const obtained = Number(val) || 0;
        const percentage = total > 0 ? (obtained / total) * 100 : 0;
        return (
          <span>
            {val !== null && val !== undefined && Number(val) !== 0 ? (
              <Button
                type="default"
                className="text-[#00C6AE] border-none !bg-transparent shadow-none
              underline underline-offset-4 decoration-[#00C6AE] p-0"
                onClick={() =>
                  handleOpen({
                    type: "EVALUATE",
                    data: {
                      id: record?.interview_id,
                      applicant_name: record?.applicant_name,
                      interview_type: record?.interview_type,
                    },
                  })
                }
              >
                {percentage.toFixed(0)}%
              </Button>
            ) : (
              <div>N/A</div>
            )}
          </span>
        );
      },
    },
    {
      title: "Final Status",
      dataIndex: "final_status",
      align: "center",
      render: (val: string) => (
        <StatusTag value={val} config={INTERVIEW_STATUS_CONFIG} />
      ),
    },
    {
      title: "Actions",
      dataIndex: "final_status",
      align: "end",
      render: (val, record) => (
        <Flex gap={12} justify="end" align="center">
          {/* See Offer Letter – only when offer letter has been sent */}
          {record?.is_offer_letter_send && (
            <Button
              type="default"
              className="text-theme-primary border-none !bg-transparent
              shadow-none underline underline-offset-4 decoration-theme-primary p-0"
              onClick={() =>
                handleOpen({
                  type: "VIEW_OFFER",
                  data: { offer_letter_id: record?.offer_letter_id },
                })
              }
            >
              View Offer Letter
            </Button>
          )}

          {/* Send Offer Letter – active only when final_status is passed AND offer not yet sent */}
          {val === "passed" && !record?.is_offer_letter_send ? (
            <Button
              type="default"
              className="text-[#00C6AE] border-none !bg-transparent
              shadow-none underline underline-offset-4 decoration-[#00C6AE] p-0"
              onClick={() =>
                handleOpen({
                  type: "OFFER",
                  data: record,
                })
              }
            >
              Send Offer Letter
            </Button>
          ) : (
            !record?.is_offer_letter_send && (
              <Button
                disabled
                type="default"
                className="!text-[#979797] border-none !bg-transparent
                shadow-none underline underline-offset-4 decoration-[#979797] p-0"
              >
                Send Offer Letter
              </Button>
            )
          )}
        </Flex>
      ),
    },
  ];

  return (
    <div>
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        tableRootClassName="border-0 p-0"
        dataSource={evaluatedData?.data?.data || []}
        onPaginate={handlePagination}
        customTitle="Interview Scores"
        loading={isFetching}
        total={evaluatedData?.data?.meta?.total || 0}
      />

      <SetupInterview
        open={config.open && config.type === "ADD"}
        handleClose={handleClose}
      />
      <GiveMark
        isView
        id={config.data?.id}
        applicantName={config.data?.applicant_name}
        open={config.open && config.type === "ASSESSMENT"}
        handleClose={handleClose}
      />
      <ViewEvaluatedMarks
        applicantName={config.data?.applicant_name}
        id={config.data?.id}
        open={config.open && config.type === "EVALUATE"}
        handleClose={handleClose}
        interviewType={config.data?.interview_type}
      />
      <SendOfferLetter
        data={config.data}
        open={config.open && config.type === "OFFER"}
        handleClose={handleClose}
      />
      <OfferLetterDrawer
        open={config.open && config.type === "VIEW_OFFER"}
        onClose={handleClose}
        offerLetterId={config.data?.offer_letter_id ?? null}
      />
    </div>
  );
}
